import { useTranslation } from 'react-i18next';

import CloseSvg from '@/shared/assets/icons/ui-kit/close-modal.svg';
import { Box } from '@/shared/ui/box';
import { Button } from '@/shared/ui/button';
import { Text } from '@/shared/ui/text';

import { createModalHook } from '../../lib/create-modal-hook.helper';
import * as Styled from './side-modal.styles';
import { SideModalProps } from './side-modal.types';

export const SideModalComponent = ({
	title,
	description,
	onClose,
	onConfirm,
	okText,
	cancelText = 'Cancel',
	children,
	isLoading,
	disabled,
	width = '480px',
	sizeTitle = 'm',
	headerContent,
	footerText,
	footerStart,
	okButtonSize = 'xs',
}: SideModalProps) => {
	const { t } = useTranslation('common');

	return (
		<Styled.Wrapper direction="column" width={width}>
			<Styled.Header align="flex-start" justify="space-between" gap={16} padding="20px 24px">
				<Box direction="column" gap={8} flex={1}>
					<Text size={sizeTitle} label={title} weight={600} />
					{description && <Text label={description} color="secondary" size="s" />}
				</Box>
				<Box align="center" gap={12}>
					{headerContent}
					<Styled.CloseIcon Svg={CloseSvg} onClick={onClose} />
				</Box>
			</Styled.Header>
			<Styled.Body direction="column" padding="24px">
				{children}
			</Styled.Body>
			{footerText && (
				<Box padding="0 24px 24px">
					<Text label={footerText} color="secondary" size="s" />
				</Box>
			)}
			<Styled.Footer justify="end" align="center" gap={16} padding="16px 24px">
				{footerStart && <Box flex={1}>{footerStart}</Box>}
				<Button
					label={cancelText || t('modal.button.cancel')}
					variant="outlined"
					size="xs"
					onClick={onClose}
					disabled={isLoading}
				/>
				<Button
					label={okText || t('modal.button.ok')}
					size={okButtonSize}
					variant="contained"
					onClick={onConfirm ?? onClose}
					disabled={disabled}
					isLoading={isLoading}
				/>
			</Styled.Footer>
		</Styled.Wrapper>
	);
};

export default createModalHook<SideModalProps>((props) => () => <SideModalComponent {...props} />, {
	variant: 'side',
});
